/**
 * Lightweight ANSI escape code to HTML span converter.
 * Handles: bold, italic, underline, standard 8/16 colors,
 * 256-color palette, and truecolor (24-bit) foreground/background.
 *
 * Returns an HTML string safe for rendering with dangerouslySetInnerHTML
 * (all text content is HTML-escaped).
 *
 * The 16-color palette is theme-aware: matcha2 (dark Shell) keeps the
 * GNOME-tango defaults that read as bright on a dark terminal; matcha2-light
 * (cream Shell) swaps in darker bases so colors read against the cream
 * background. Default white in matcha2's palette (#d3d7cf) was invisible on
 * cream — that's the bug this swap fixes.
 */

// Tango palette — bright bases for dark terminal backgrounds.
const ANSI_COLORS_16_DARK = [
  "#000000",
  "#cc0000",
  "#4e9a06",
  "#c4a000",
  "#3465a4",
  "#75507b",
  "#06989a",
  "#d3d7cf",
  "#555753",
  "#ef2929",
  "#8ae234",
  "#fce94f",
  "#729fcf",
  "#ad7fa8",
  "#34e2e2",
  "#eeeeec",
];

// Solarized-leaning palette — darker bases that read on cream surfaces.
const ANSI_COLORS_16_LIGHT = [
  "#222222",
  "#a31515",
  "#1f7d3a",
  "#a06a00",
  "#0050b3",
  "#762d8a",
  "#0a7472",
  "#5c5c5c",
  "#444444",
  "#c12d2d",
  "#287d3c",
  "#a06a00",
  "#1d6dd0",
  "#933899",
  "#108785",
  "#3a3a3a",
];

function ansiPalette(): string[] {
  if (typeof document === "undefined") return ANSI_COLORS_16_DARK;
  const theme =
    document.documentElement.getAttribute("data-theme") ?? "matcha2";
  return theme.endsWith("-light") ? ANSI_COLORS_16_LIGHT : ANSI_COLORS_16_DARK;
}

function color256(n: number, palette: string[]): string {
  if (n < 16) return palette[n] ?? palette[7] ?? "#d3d7cf";
  if (n < 232) {
    const idx = n - 16;
    const r = Math.floor(idx / 36) * 51;
    const g = (Math.floor(idx / 6) % 6) * 51;
    const b = (idx % 6) * 51;
    return `rgb(${r},${g},${b})`;
  }
  const gray = (n - 232) * 10 + 8;
  return `rgb(${gray},${gray},${gray})`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function ansiToHtml(ansi: string): string {
  const palette = ansiPalette();
  const parts: string[] = [];
  let fg: string | null = null;
  let bg: string | null = null;
  let bold = false;
  let italic = false;
  let underline = false;
  let spanOpen = false;

  const openSpan = () => {
    const styles: string[] = [];
    if (fg) styles.push(`color:${fg}`);
    if (bg) styles.push(`background-color:${bg}`);
    if (bold) styles.push("font-weight:500");
    if (italic) styles.push("font-style:italic");
    if (underline) styles.push("text-decoration:underline");
    if (styles.length > 0) {
      parts.push(`<span style="${styles.join(";")}">`);
      spanOpen = true;
    }
  };

  const closeSpan = () => {
    if (spanOpen) {
      parts.push("</span>");
      spanOpen = false;
    }
  };

  // eslint-disable-next-line no-control-regex
  const regex = /\x1b\[([0-9;]*)m/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(ansi)) !== null) {
    // Emit text before this escape
    if (match.index > lastIndex) {
      openSpan();
      parts.push(escapeHtml(ansi.slice(lastIndex, match.index)));
    }
    lastIndex = match.index + match[0].length;

    closeSpan();

    // Parse SGR codes
    const codes = (match[1] || "0").split(";").map(Number);
    for (let i = 0; i < codes.length; i++) {
      const c = codes[i] ?? 0;
      if (c === 0) {
        fg = null;
        bg = null;
        bold = false;
        italic = false;
        underline = false;
      } else if (c === 1) {
        bold = true;
      } else if (c === 3) {
        italic = true;
      } else if (c === 4) {
        underline = true;
      } else if (c >= 30 && c <= 37) {
        fg = palette[c - 30] ?? null;
      } else if (c >= 40 && c <= 47) {
        bg = palette[c - 40] ?? null;
      } else if (c >= 90 && c <= 97) {
        fg = palette[c - 90 + 8] ?? null;
      } else if (c >= 100 && c <= 107) {
        bg = palette[c - 100 + 8] ?? null;
      } else if (c === 38) {
        // Extended foreground
        const mode = codes[i + 1];
        if (mode === 5 && codes[i + 2] !== undefined) {
          fg = color256(codes[i + 2]!, palette);
          i += 2;
        } else if (
          mode === 2 &&
          codes[i + 2] !== undefined &&
          codes[i + 3] !== undefined &&
          codes[i + 4] !== undefined
        ) {
          fg = `rgb(${codes[i + 2]},${codes[i + 3]},${codes[i + 4]})`;
          i += 4;
        }
      } else if (c === 48) {
        // Extended background
        const mode = codes[i + 1];
        if (mode === 5 && codes[i + 2] !== undefined) {
          bg = color256(codes[i + 2]!, palette);
          i += 2;
        } else if (
          mode === 2 &&
          codes[i + 2] !== undefined &&
          codes[i + 3] !== undefined &&
          codes[i + 4] !== undefined
        ) {
          bg = `rgb(${codes[i + 2]},${codes[i + 3]},${codes[i + 4]})`;
          i += 4;
        }
      } else if (c === 39) {
        fg = null;
      } else if (c === 49) {
        bg = null;
      }
    }
  }

  // Emit remaining text
  if (lastIndex < ansi.length) {
    openSpan();
    parts.push(escapeHtml(ansi.slice(lastIndex)));
  }
  closeSpan();

  return parts.join("");
}

/**
 * Strip ANSI escape codes, returning plain text.
 */
export function stripAnsi(ansi: string): string {
  // eslint-disable-next-line no-control-regex
  return ansi.replace(/\x1b\[[0-9;]*m/g, "");
}
